import { useState, useEffect } from 'react';
import { Bell, X, Smartphone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  isPushSupported,
  isLocalhostEnvironment,
  requestPushPermission,
  registerServiceWorker,
  subscribeToPush,
  savePushSubscription,
  getCurrentPushSubscription,
} from '../lib/pushService';

const STORAGE_KEY = 'push-onboarding-state';

type OnboardingState = {
  dismissed: boolean;
  enabled: boolean;
  timestamp: number;
};

function getState(): OnboardingState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setState(partial: Partial<OnboardingState>) {
  const current = getState() || { dismissed: false, enabled: false, timestamp: 0 };
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, ...partial, timestamp: Date.now() }));
}

export default function PushOnboardingPrompt() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!isPushSupported()) return;
    if (isLocalhostEnvironment()) return;

    const state = getState();
    if (state?.enabled || state?.dismissed) {
      // If dismissed, re-show after 30 days
      if (state.dismissed && !state.enabled && Date.now() - state.timestamp > 30 * 24 * 60 * 60 * 1000) {
        setVisible(true);
        return;
      }
      return;
    }

    // Check if already subscribed
    getCurrentPushSubscription().then(sub => {
      if (sub) {
        setState({ enabled: true });
      } else {
        // Show after a short delay so it doesn't compete with page load
        const timer = setTimeout(() => setVisible(true), 3000);
        return () => clearTimeout(timer);
      }
    });
  }, [user]);

  const handleEnable = async () => {
    setActivating(true);
    setError(null);

    try {
      const permission = await requestPushPermission();
      if (permission === 'denied') {
        setError('Push notificaties zijn geblokkeerd. Wijzig dit in je browserinstellingen.');
        setActivating(false);
        return;
      }
      if (permission !== 'granted') {
        setError('Toestemming niet verleend.');
        setActivating(false);
        return;
      }

      const registration = await registerServiceWorker();
      if (!registration) {
        setError('Service worker kon niet worden geregistreerd.');
        setActivating(false);
        return;
      }

      const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
      if (!vapidKey) {
        setError('Push configuratie ontbreekt.');
        setActivating(false);
        return;
      }

      let subscription: PushSubscription;
      try {
        subscription = await subscribeToPush(registration, vapidKey);
      } catch (subErr) {
        setError(`Push abonnement fout: ${subErr instanceof Error ? subErr.message : 'Onbekend'}`);
        setActivating(false);
        return;
      }

      const saved = await savePushSubscription(subscription);
      if (!saved) {
        setError('Kon registratie niet opslaan.');
        setActivating(false);
        return;
      }

      setState({ enabled: true });
      setSuccess(true);
      setTimeout(() => setVisible(false), 2000);
    } catch (err) {
      console.error('Push onboarding error:', err);
      setError('Er ging iets mis. Probeer het later opnieuw.');
    } finally {
      setActivating(false);
    }
  };

  const handleDismiss = () => {
    setState({ dismissed: true });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-50 sm:max-w-sm animate-slide-up safe-bottom">
      <div className="bg-surface border border-theme-border rounded-xl shadow-2xl overflow-hidden">
        {success ? (
          <div className="px-4 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <Bell className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-sm font-medium text-green-700">Push notificaties zijn ingeschakeld!</p>
          </div>
        ) : (
          <>
            <div className="px-4 pt-4 pb-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F598AF]/15 flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-5 h-5 text-[#F598AF]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-foreground">Push notificaties inschakelen?</p>
                    <button
                      onClick={handleDismiss}
                      className="p-0.5 text-foreground-tertiary hover:text-foreground-secondary rounded flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-foreground-tertiary mt-1 leading-relaxed">
                    Ontvang meldingen over offertes, contracten en projectupdates direct op je telefoon.
                  </p>
                </div>
              </div>
              {error && (
                <p className="text-xs text-red-600 mt-2 ml-[52px]">{error}</p>
              )}
            </div>
            <div className="px-4 pb-4 flex gap-2 ml-[52px]">
              <button
                onClick={handleEnable}
                disabled={activating}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white text-sm font-semibold rounded-lg hover:bg-brand-active transition-colors disabled:opacity-50"
              >
                <Bell className="w-3.5 h-3.5" />
                {activating ? 'Activeren...' : 'Inschakelen'}
              </button>
              <button
                onClick={handleDismiss}
                className="px-3 py-1.5 text-sm text-foreground-tertiary hover:text-foreground-secondary transition-colors"
              >
                Later
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
